// Extrait de ClinicAdminDashboard.js (handover bloc F3-A) — déplacé tel quel, état interne inchangé.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Calendar } from "lucide-react";

export default function ReminderTemplatesEditor() {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [previewType, setPreviewType] = useState(null); // {type, timing, text}

  useEffect(() => {
    (async () => {
      try {
        const templatesRes = await api.get("/appointments/reminder-templates");
        setTemplates(templatesRes.data.templates);
        setIsCustom(templatesRes.data.isCustom);
      } catch {
        toast.error("Erreur chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/appointments/reminder-templates", templates);
      toast.success("Templates de rappels sauvegardés");
      setIsCustom(true);
    } catch {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateTemplate = (type, timing, value) => {
    setTemplates(prev => ({
      ...prev,
      [type]: { ...prev[type], [timing]: value },
    }));
  };

  const TYPE_LABELS = {
    consultation: "Consultation",
    imagerie: "Imagerie",
    analyses: "Analyses / Labo",
    chimio_hdj: "Chimio / HDJ",
    radiotherapie: "Radiotherapie",
  };
  const TIMING_LABELS = { j1: "J-1 (la veille, notification push)", j0: "Jour J (le matin, notification push)" };
  const TIMING_COLORS = { j1: "border-orange-300 bg-orange-50", j0: "border-red-300 bg-red-50" };
  const VARIABLES = [
    { var: "{prenom}", desc: "Prénom du patient" },
    { var: "{date}", desc: "Date du RDV" },
    { var: "{heure}", desc: "Heure du RDV" },
    { var: "{link_cancel}", desc: "Lien d'annulation" },
  ];

  if (loading) return <div className="dash-card text-center py-10"><div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="reminder-templates-editor">
      {/* Header */}
      <div className="dash-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-slate-800 text-lg">Templates de rappels</h3>
            <p className="text-sm text-slate-500 mt-1">
              Personnalisez les notifications push envoyées aux patients.
              {isCustom ? " (personnalisés)" : " (par défaut)"}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-templates-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>

        {/* Variables available */}
        <div className="bg-slate-50 rounded-xl p-3 mb-4">
          <p className="text-xs font-semibold text-slate-500 mb-2">Variables disponibles</p>
          <div className="flex flex-wrap gap-2">
            {VARIABLES.map(v => (
              <span key={v.var} className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600">
                <code className="font-mono text-emerald-600">{v.var}</code> {v.desc}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Templates by type */}
      {templates && Object.entries(TYPE_LABELS).map(([type, typeLabel]) => (
        <div key={type} className="dash-card" data-testid={`template-section-${type}`}>
          <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            {typeLabel}
          </h4>
          <div className="space-y-4">
            {Object.entries(TIMING_LABELS).map(([timing, timingLabel]) => {
              const value = templates[type]?.[timing] || "";
              return (
                <div key={timing} className={`border-l-4 rounded-r-xl p-4 ${TIMING_COLORS[timing]}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">{timingLabel}</label>
                    <button
                      onClick={() => setPreviewType(previewType?.timing === timing && previewType?.type === type ? null : { type, timing, text: value })}
                      className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                    >
                      {previewType?.timing === timing && previewType?.type === type ? "Masquer" : "Aperçu"}
                    </button>
                  </div>
                  <textarea
                    value={value}
                    onChange={e => updateTemplate(type, timing, e.target.value)}
                    data-testid={`template-${type}-${timing}`}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                  {/* Preview */}
                  {previewType?.timing === timing && previewType?.type === type && (
                    <div className="mt-2 p-3 bg-white rounded-xl border border-slate-200" data-testid={`preview-${type}-${timing}`}>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Aperçu notification</p>
                      <p className="text-sm text-slate-700">
                        {value
                          .replace("{prenom}", "Fatima")
                          .replace("{date}", "Vendredi 25 avril")
                          .replace("{heure}", "10h30")
                          .replace("{link_cancel}", "https://hana.ma/a/abc123")
                        }
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
